import React from "react";
import { Box, useTheme, useMediaQuery } from "@mui/material";
import { inject, observer } from "mobx-react";

import { FEATURE, FilterConditionKey, Inventory, INVENTORY_TERRAFORM_STATUS, UI_TABLES } from "store/types";
import {
  PossibleJsonValue,
  ProviderServiceTypeIcon,
  Status,
  SUPPORTED_STATUSES_TYPE,
  Table,
  TagsCountList,
  Price,
  TableFields,
  AccountDetailsOnHover,
} from "components/ui";
import { InventoryAdditionalInfo, InventoryAdditionalInfoHeader } from "routes/InventoryRoute/components";
import { formattedDate } from "tools";
import { getIconTypeByResource } from "tools/utils";
import { RootStore } from "store/root.store";
import mainStore from "store";

type InventoryTableProps = {
  store?: RootStore;
  widgetId?: string;
  relatedResources?: boolean;
  clickOnTag?: (property: string, value: PossibleJsonValue) => void;
  isDynamicFields?: boolean;
  data: Inventory[];
  filterSidebarOpen?: boolean;
  filterSidebarWidth?: number;
  noDataText?: string;
  showDeletedAt?: boolean;
  selectedAccounts?: string[];
};

export const inventoryFields = (
  integrationTerraformEnabled: boolean,
  showDeletedAt = false,
  showAccountColumn = false,
): TableFields[] => [
  ...(showAccountColumn
    ? [
        {
          name: "Account",
          property: "provider_account_id",
          textValue: (item: Inventory) => {
            const account = mainStore.accounts.activeAccounts?.find(
              (a) => a.provider_account_id === item.provider_account_id,
            );
            return account?.name || item.provider_account_id;
          },
          render: (item: Inventory) => {
            const account = mainStore.accounts.activeAccounts?.find(
              (a) => a.provider_account_id === item.provider_account_id,
            );
            return (
              <AccountDetailsOnHover
                inTable
                id={account?.id || ""}
                component={<Box>{account?.name || item.provider_account_id}</Box>}
              />
            );
          },
          additionalInfo: "table",
        },
      ]
    : []),
  {
    name: "Type",
    property: "type",
    textValue: (item: Inventory) => item.type,
    render: (item: Inventory) => <ProviderServiceTypeIcon type={getIconTypeByResource(item.type)} text={item.type} />,
    filterConditions: ["IS"] as FilterConditionKey[],
  },
  {
    name: "Name",
    property: "name",
    breakWordsLine: true,
  },
  {
    name: "Location",
    property: "location",
    filterConditions: ["IS"] as FilterConditionKey[],
  },
  {
    name: "Tags",
    property: "tags",
    render: (item: Inventory) => <TagsCountList tags={item.tags} />,
    textValue: (item: Inventory) => (item.tags ? JSON.stringify(item.tags) : ""),
    notSortable: true,
    searchableObject: true,
    filterConditions: ["IS"] as FilterConditionKey[],
  },
  ...(integrationTerraformEnabled
    ? [
        {
          name: "IaC Status",
          property: "iacStatus",
          render: (item: Inventory) => (
            <Status
              status={item.terraform_status || INVENTORY_TERRAFORM_STATUS.NOT_CONNECTED}
              statusType={SUPPORTED_STATUSES_TYPE.TERRAFORM}
            />
          ),
          textValue: (item: Inventory) => item.terraform_status || INVENTORY_TERRAFORM_STATUS.NOT_CONNECTED,
          notSortable: true,
          additionalInfo: "table",
        },
      ]
    : []),
  {
    name: "Price (month to date)",
    property: "month_to_date_cost",
    render: (item: Inventory) => <Price price={item.month_to_date_cost} />,
    textValue: (item: Inventory) => item.month_to_date_cost?.toString() || "",
    additionalInfo: "table",
    sortByNumber: true,
  },
  {
    name: "Created At",
    property: "created_at",
    render: (item: Inventory) => formattedDate(item.created_at),
    additionalInfo: "table",
  },
  {
    name: "Updated At",
    property: "updated_at",
    render: (item: Inventory) => formattedDate(item.updated_at),
    additionalInfo: "table",
    defaultSort: -1,
  },
  ...(showDeletedAt
    ? [
        {
          name: "Deleted At",
          property: "deleted_at",
          render: (item: Inventory) => (item.deleted_at ? formattedDate(item.deleted_at) : ""),
          textValue: (item: Inventory) => (item.deleted_at ? formattedDate(item.deleted_at) : ""),
          additionalInfo: "table",
        },
      ]
    : []),
];

const InventoryTable = ({
  store,
  data,
  widgetId,
  relatedResources,
  clickOnTag,
  isDynamicFields,
  filterSidebarOpen,
  filterSidebarWidth = 320,
  noDataText,
  showDeletedAt = false,
  selectedAccounts = [],
  ...props
}: InventoryTableProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("laptop"));
  const isSmallNav = store?.ui.smallView;

  // Calculate dynamic width based on navigation and filter panel states
  const getMaxWidth = () => {
    if (isMobile) {
      return "calc(100vw - 32px)"; // Mobile: viewport minus page margins
    }

    // Desktop calculations
    const navWidth = isSmallNav ? 69 : 280; // MENU_SMALL_VIEW_WIDTH : MENU_WIDTH
    const filterWidth = filterSidebarOpen ? filterSidebarWidth : 40; // Filter panel width
    const contentPadding = 40; // Content area padding (20px each side)
    const margins = 32; // Page margins

    const totalUsedWidth = navWidth + filterWidth + contentPadding + margins;
    return `calc(100vw - ${totalUsedWidth}px)`;
  };

  return (
    <Table
      {...props}
      removePadding
      isDynamicFields={isDynamicFields}
      maxWidthFn={getMaxWidth}
      type={
        relatedResources
          ? UI_TABLES.INVENTORY_RELATED_RESOURCES
          : widgetId
          ? `${UI_TABLES.INVENTORY}_${widgetId}`
          : UI_TABLES.INVENTORY
      }
      noDataText={noDataText || (relatedResources ? "No related resources found" : "No resources found")}
      fields={inventoryFields(
        !!store?.auth.isFeatureEnabled(FEATURE.INTEGRATIONS_TERRAFORM),
        showDeletedAt,
        selectedAccounts.length > 1,
      ).filter(
        (f) =>
          (!relatedResources && !widgetId) || ["name", "type", "location", "provider_account_id"].includes(f.property),
      )}
      data={data}
      additionalInfo={isDynamicFields ? undefined : <InventoryAdditionalInfo clickOnTag={clickOnTag} />}
      additionalInfoHeader={isDynamicFields ? undefined : <InventoryAdditionalInfoHeader />}
    />
  );
};

export default inject("store")(observer(InventoryTable));
